/** @jsx jsx */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import {
  useTheme,
  useMediaQuery,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  Chip,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DoneIcon from '@material-ui/icons/Done';
import CrossIcon from '@material-ui/icons/Clear';

import {
  ICustomTheme,
  IChallengeDeclaration,
  DeclarationStatusType,
} from 'interfaces';
import useQuery from 'core/utils/use-query.hook';
import ProductsDetail from './ProductsDetail';
import QuestionsDetail from './QuestionsDetail';
import WarningTextDeclarationRefused from './WarningTextDeclarationRefused';
import { StyledBubble } from 'components/pages/History/styles';
import { RESULT_STATUS } from 'constants/index';

const BannerChip = styled(Chip)(({ theme }: { theme: ICustomTheme }) => ({
  background: `${theme.colors.paletteFromBO.main}12`,
  color: theme.colors.paletteFromBO.main,
  fontWeight: 'bold',
  fontSize: 18,
  marginRight: theme.spacing(1),
  padding: theme.spacing(2, 2),
  borderRadius: 30,
  cursor: 'pointer',
  minWidth: '14ch',
}));

function DeclarationsStatus({
  status,
}: {
  status: DeclarationStatusType;
}): JSX.Element {
  const theme: ICustomTheme = useTheme();

  const Icon = status === 'refused' ? CrossIcon : DoneIcon;

  return (
    <StyledBubble
      css={{
        backgroundColor: (() => {
          switch (status) {
            case 'waiting':
              return theme.colors.grey.light;
            case 'refused':
              return theme.colors.error.main;
            default:
              return theme.colors.success.main;
          }
        })(),
      }}
    >
      <Icon />
    </StyledBubble>
  );
}

function DeclarationSummary({
  id,
  createdAt,
  declarationRewardValue,
  declarationResultValue,
  status,
  unitToDisplay,
  isGoalMecanicByProduct,
}: IChallengeDeclaration & {
  unitToDisplay: string;
  isGoalMecanicByProduct: boolean;
}): JSX.Element {
  const theme: ICustomTheme = useTheme();
  const { t } = useTranslation();
  const isValidatedDeclaration = status === RESULT_STATUS.VALIDATED;

  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      css={{ gap: theme.spacing(2), marginRight: theme.spacing(1.5) }}
    >
      <Grid item>
        <Grid container alignItems="center" css={{ gap: theme.spacing(2) }}>
          <Grid item>
            <DeclarationsStatus status={status} />
          </Grid>
          <Grid item>
            <Typography>
              {t('challenges.declaration.history.declaration_date', {
                id,
                date: new Date(createdAt).toLocaleDateString(),
              })}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        {isValidatedDeclaration && (
          <Grid container justifyContent="center" alignItems="center">
            <Grid item>
              <BannerChip
                label={
                  <NumberFormat
                    value={
                      isGoalMecanicByProduct
                        ? declarationRewardValue || 0
                        : declarationResultValue || 0
                    }
                    displayType="text"
                    thousandSeparator=" "
                  />
                }
              />
            </Grid>
            <Grid item>
              <Typography
                variant="caption"
                style={{ color: theme.colors.paletteFromBO.main }}
              >
                {unitToDisplay} {isGoalMecanicByProduct && '*'}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

export default function Declarations({
  isGoalMecanicByProduct,
  declarations,
  unitToDisplay,
}: {
  isGoalMecanicByProduct: boolean;
  declarations: IChallengeDeclaration[];
  unitToDisplay: any;
}): JSX.Element {
  const { t } = useTranslation();
  const isDetailsMinifiedMode = useMediaQuery('(max-width:1100px)');
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const { type } = useQuery();
  const filteredDeclarations = useMemo(() => {
    const reversedDeclarations = [...declarations].reverse();

    return !type || type === 'all'
      ? reversedDeclarations
      : reversedDeclarations.filter(
          ({ status }: IChallengeDeclaration) => status === type,
        );
  }, [type, declarations]);

  return (
    <React.Fragment>
      {filteredDeclarations.map(
        (declaration: IChallengeDeclaration, index: number) => (
          <Accordion
            defaultExpanded={!index && !type}
            key={declaration.id}
            square
            css={{
              boxShadow: 'none',
              borderBottom: `1px solid ${theme.colors.grey.light}`,
              '&:first-of-type': {
                borderTop: `1px solid ${theme.colors.grey.light}`,
              },
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon style={{ fontSize: 30 }} />}
              css={{
                marginRight: theme.spacing(1),
                padding: theme.spacing(1, 0),
              }}
            >
              <DeclarationSummary
                {...declaration}
                unitToDisplay={unitToDisplay}
                isGoalMecanicByProduct={isGoalMecanicByProduct}
              />
            </AccordionSummary>
            {declaration.status === RESULT_STATUS.REFUSED &&
              declaration.comment && (
                <WarningTextDeclarationRefused
                  rejectionText={declaration.comment}
                />
              )}
            <ProductsDetail
              {...declaration}
              isMobile={isDetailsMinifiedMode}
              unitToDisplay={
                isGoalMecanicByProduct ? `${unitToDisplay}*` : unitToDisplay
              }
              isGoalMecanicByProduct={isGoalMecanicByProduct}
            />
            {declaration.formEntry?.formValues?.length && (
              <QuestionsDetail
                questions={declaration.formEntry?.formValues}
                isMobile={isDetailsMinifiedMode}
              />
            )}
            {isGoalMecanicByProduct && (
              <div
                style={{
                  margin: theme.spacing(2, 0),
                  fontSize: isMobile ? 14 : 16,
                }}
              >
                {t('challenges.declaration.history.thresholdInfo', {
                  rewardUnit: unitToDisplay,
                })}
              </div>
            )}
          </Accordion>
        ),
      )}
    </React.Fragment>
  );
}
